import React from 'react';
import PropTypes from 'prop-types';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import LinkToModuleResource from '../../../common/resource/LinkToModuleResource';
import {
  BAGGAGE_PATH,
  ITINERARY_FOR_COUNTER_PATH,
  TICKET_PATH,
} from '../../../../config/paths';
import Table from '../../../common/Table';
import {
  DEFAULT_PAGE_SIZE,
  ITEM_CONTENT_TYPE,
  ITEM_INCIDENT_TYPE,
  NEW_BAGGAGE_ITEM_STATUS,
} from '../../../../config/constants';
import Checkbox from '../../../common/forms/Checkbox';
import EnumStatus from '../../../common/EnumStatus';
import DateCell from '../../../common/cells/DateCell';
import { DATE_TIME_FORMAT } from '../../../../config/locale';
import ItemCommentForm from './ItemCommentForm';

const ItemBasicInformationResource = ({
  id,
  itemMovementList,
  sourceLocation,
  destinationLocation,
  mainTicket,
  itemContent,
  itineraryId,
  isSpecial,
  itemCorrelation,
  status,
  weight,
  baggageId,
  itemCategoryName,
  description,
  itemConditionList,
  conditionDescription,
  itemCommentDtoList,
  handleItemCommentSubmit,
  itemIncidentList,
}) => (
  <>
    <ResourceProperty label="ID:">{id}</ResourceProperty>
    <ResourceProperty label="Equipaje:">
      <LinkToModuleResource
        text={baggageId}
        href={`${BAGGAGE_PATH}/${baggageId}`}
        isExternalPath
      />
    </ResourceProperty>
    <ResourceProperty label="Correlativo:">{itemCorrelation}</ResourceProperty>
    <ResourceProperty label="Descripción:">{description}</ResourceProperty>
    <ResourceProperty label="Categoria:">{itemCategoryName}</ResourceProperty>
    <ResourceProperty label="Peso:">{weight}</ResourceProperty>
    <ResourceProperty label="Contenido">
      <EnumStatus enumObject={ITEM_CONTENT_TYPE} enumValue={itemContent} />
    </ResourceProperty>
    <ResourceProperty label="Condiciones:">
      <ul>
        {itemConditionList.map((condition) => (
          <li key={condition.id}>{condition.name}</li>
        ))}
      </ul>
    </ResourceProperty>
    <ResourceProperty label="Descripción de Condición:">
      {conditionDescription || '-'}
    </ResourceProperty>
    <ResourceProperty label="Especial:">
      <Checkbox checked={isSpecial} disabled />
    </ResourceProperty>
    <ResourceProperty label="Estado:">
      <EnumStatus enumObject={NEW_BAGGAGE_ITEM_STATUS} enumValue={status} />
    </ResourceProperty>
    <h3>Detalles del Viaje</h3>
    <ResourceProperty label="Origen:">
      {sourceLocation ? sourceLocation.name : '-'}
    </ResourceProperty>
    <ResourceProperty label="Destino:">
      {destinationLocation ? destinationLocation.name : '-'}
    </ResourceProperty>
    <ResourceProperty label="Boleto Principal:">
      {mainTicket ? (
        <LinkToModuleResource
          text={mainTicket.ticketCode}
          href={`${TICKET_PATH}/${mainTicket.id}`}
          isExternalPath
        />
      ) : (
        '-'
      )}
    </ResourceProperty>
    <ResourceProperty label="Itinerario ID:">
      <LinkToModuleResource
        text={itineraryId}
        href={`${ITINERARY_FOR_COUNTER_PATH}/${itineraryId}`}
        isExternalPath
      />
    </ResourceProperty>
    <h3>Detalle de Movimientos</h3>
    <Table
      data={itemMovementList}
      columns={[
        {
          Header: 'Usuario',
          accessor: 'userFullName',
          className: 'text-center',
        },
        {
          Header: 'Observación',
          accessor: 'remarks',
          className: 'text-center',
          Cell: (row) => row.value || '-',
        },
        {
          Header: 'Ubicación',
          accessor: 'location.name',
          className: 'text-center',
          Cell: (row) => row.value || '-',
        },
        {
          Header: 'Itinerario ID',
          accessor: 'itemItineraryId',
          className: 'text-center',
          Cell: (row) =>
            row.value ? (
              <LinkToModuleResource
                text={row.value}
                href={`${ITINERARY_FOR_COUNTER_PATH}/${row.value}`}
                isExternalPath
              />
            ) : (
              '-'
            ),
        },
        {
          Header: 'Estado',
          accessor: 'itemStatus',
          className: 'text-center',
          Cell: (row) => (
            <EnumStatus
              enumObject={NEW_BAGGAGE_ITEM_STATUS}
              enumValue={row.value}
            />
          ),
        },
        {
          Header: 'Fecha',
          accessor: 'createDate',
          className: 'text-center',
          Cell: (row) => (
            <DateCell value={row.value} format={DATE_TIME_FORMAT} />
          ),
        },
      ]}
      defaultPageSize={DEFAULT_PAGE_SIZE}
      manual={false}
      filterable={false}
    />
    <br />
    <h3>Detalle de Incidencias</h3>
    <Table
      data={itemIncidentList}
      columns={[
        {
          Header: 'Tipo de Incidencia',
          accessor: 'itemIncidentType',
          className: 'text-center',
          Cell: (row) => (
            <EnumStatus enumObject={ITEM_INCIDENT_TYPE} enumValue={row.value} />
          ),
        },
        {
          Header: 'Descripción',
          accessor: 'description',
          className: 'text-center',
          Cell: (row) => row.value || '-',
        },
        {
          Header: 'Usuario',
          accessor: 'user.fullName',
          className: 'text-center',
          Cell: (row) => row.value || '-',
        },
        {
          Header: 'Fecha',
          accessor: 'createDate',
          className: 'text-center',
          Cell: (row) => (
            <DateCell value={row.value} format={DATE_TIME_FORMAT} />
          ),
        },
      ]}
      defaultPageSize={DEFAULT_PAGE_SIZE}
      manual={false}
      filterable={false}
    />
    <h3>Comentarios</h3>
    <ItemCommentForm
      comments={itemCommentDtoList || []}
      onSubmit={handleItemCommentSubmit}
    />
  </>
);

ItemBasicInformationResource.propTypes = {
  id: PropTypes.string.isRequired,
  itemMovementList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  sourceLocation: PropTypes.string.isRequired,
  destinationLocation: PropTypes.string.isRequired,
  mainTicket: PropTypes.string.isRequired,
  itemContent: PropTypes.string.isRequired,
  itineraryId: PropTypes.string.isRequired,
  isSpecial: PropTypes.bool.isRequired,
  itemCorrelation: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  weight: PropTypes.number.isRequired,
  baggageId: PropTypes.string.isRequired,
  itemCategoryName: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  conditionDescription: PropTypes.string.isRequired,
  itemConditionList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  itemCommentDtoList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      comment: PropTypes.string.isRequired,
      createDate: PropTypes.number.isRequired,
      user: PropTypes.shape({
        fullName: PropTypes.string.isRequired,
      }),
    }),
  ).isRequired,
  handleItemCommentSubmit: PropTypes.func.isRequired,
  itemIncidentList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      itemIncidentType: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      user: PropTypes.shape({
        fullName: PropTypes.string.isRequired,
      }),
      createDate: PropTypes.number.isRequired,
    }),
  ).isRequired,
};

export default ItemBasicInformationResource;
